// Multi-year CWE Top 25 data with historical tracking
// Sources:
// - 2024: https://cwe.mitre.org/top25/archive/2024/2024_top25_list.html
// - 2023: https://cwe.mitre.org/top25/archive/2023/2023_top25_list.html

import { TOP_25_2024 } from './top25-2024';
import { TOP_25_2023 } from './top25-2023';
import { Top25Entry } from '../lib/types';

export interface Top25YearData {
  year: number;
  releaseDate: string;
  dataSource: string; // e.g., "CVE records from 2023-2024"
  totalCVEs: number;
  entries: Top25Entry[];
}

export const TOP_25_BY_YEAR: Record<number, Top25YearData> = {
  2024: {
    year: 2024,
    releaseDate: '2024-06-25',
    dataSource: 'CVE records from 2023-2024',
    totalCVEs: 31770,
    entries: TOP_25_2024,
  },
  2023: {
    year: 2023,
    releaseDate: '2023-06-21',
    dataSource: 'NVD data from 2021-2022',
    totalCVEs: 43996,
    entries: TOP_25_2023,
  },
};

export const AVAILABLE_YEARS = [2024, 2023];
export const LATEST_YEAR = 2024;

/**
 * Get Top 25 data for a specific year
 */
export function getTop25ByYear(year: number): Top25YearData | null {
  return TOP_25_BY_YEAR[year] || null;
}

/**
 * Get historical ranking for a CWE across all years
 */
export interface CWEHistoricalRanking {
  cweId: string;
  name: string;
  rankings: {
    year: number;
    rank: number | null; // null if not in Top 25 that year
    score: number;
    cveCount: number;
    avgCVSS: number;
  }[];
  trend: 'rising' | 'falling' | 'stable' | 'new' | 'dropped';
  rankChange?: number; // Change from previous year (positive = improved rank)
}

/**
 * Get historical ranking trend for a specific CWE
 */
export function getCWEHistoricalRanking(cweId: string): CWEHistoricalRanking | null {
  const rankings: CWEHistoricalRanking['rankings'] = [];

  // Collect rankings from all years
  for (const year of AVAILABLE_YEARS.sort((a, b) => a - b)) {
    const yearData = TOP_25_BY_YEAR[year];
    const entry = yearData.entries.find((e) => e.cweId === cweId);

    if (entry) {
      rankings.push({
        year,
        rank: entry.rank,
        score: entry.score,
        cveCount: entry.cveCount,
        avgCVSS: entry.avgCVSS,
      });
    } else {
      rankings.push({
        year,
        rank: null,
        score: 0,
        cveCount: 0,
        avgCVSS: 0,
      });
    }
  }

  // If CWE never appeared in Top 25, return null
  if (rankings.every((r) => r.rank === null)) {
    return null;
  }

  // Get name from most recent appearance
  const latestEntry = [...AVAILABLE_YEARS]
    .sort((a, b) => b - a)
    .map((year) => TOP_25_BY_YEAR[year].entries.find((e) => e.cweId === cweId))
    .find((entry) => entry !== undefined);

  if (!latestEntry) return null;

  // Calculate trend
  const recentRankings = rankings.slice(-2);
  let trend: CWEHistoricalRanking['trend'] = 'stable';
  let rankChange: number | undefined;

  if (recentRankings[0]?.rank === null && recentRankings[1]?.rank !== null) {
    trend = 'new';
  } else if (recentRankings[0]?.rank !== null && recentRankings[1]?.rank === null) {
    trend = 'dropped';
  } else if (recentRankings[0]?.rank !== null && recentRankings[1]?.rank !== null) {
    rankChange = recentRankings[0].rank - recentRankings[1].rank;
    if (rankChange > 0) {
      trend = 'falling'; // Higher rank number = worse position
    } else if (rankChange < 0) {
      trend = 'rising'; // Lower rank number = better position
    } else {
      trend = 'stable';
    }
  }

  return {
    cweId,
    name: latestEntry.name,
    rankings,
    trend,
    rankChange,
  };
}

/**
 * Get all CWEs that appear in any Top 25 list
 */
export function getAllTop25CWEs(): string[] {
  const cweIds = new Set<string>();

  for (const year of AVAILABLE_YEARS) {
    const yearData = TOP_25_BY_YEAR[year];
    yearData.entries.forEach((entry) => cweIds.add(entry.cweId));
  }

  return Array.from(cweIds).sort();
}

/**
 * Compare two years side by side
 */
export interface YearComparison {
  year1: number;
  year2: number;
  year1Data: Top25YearData;
  year2Data: Top25YearData;
  commonCWEs: string[]; // CWEs in both years
  newInYear2: string[]; // CWEs new to Year 2
  droppedFromYear2: string[]; // CWEs that were in Year 1 but not Year 2
  rankChanges: {
    cweId: string;
    name: string;
    year1Rank: number;
    year2Rank: number;
    change: number; // Negative = improved, Positive = worsened
  }[];
}

/**
 * Compare two specific years
 */
export function compareYears(year1: number, year2: number): YearComparison | null {
  const year1Data = TOP_25_BY_YEAR[year1];
  const year2Data = TOP_25_BY_YEAR[year2];

  if (!year1Data || !year2Data) return null;

  const year1CWEs = new Set(year1Data.entries.map((e) => e.cweId));
  const year2CWEs = new Set(year2Data.entries.map((e) => e.cweId));

  const commonCWEs = year1Data.entries
    .map((e) => e.cweId)
    .filter((id) => year2CWEs.has(id));

  const newInYear2 = year2Data.entries
    .map((e) => e.cweId)
    .filter((id) => !year1CWEs.has(id));

  const droppedFromYear2 = year1Data.entries
    .map((e) => e.cweId)
    .filter((id) => !year2CWEs.has(id));

  const rankChanges = commonCWEs.map((cweId) => {
    const year1Entry = year1Data.entries.find((e) => e.cweId === cweId)!;
    const year2Entry = year2Data.entries.find((e) => e.cweId === cweId)!;

    return {
      cweId,
      name: year2Entry.name,
      year1Rank: year1Entry.rank,
      year2Rank: year2Entry.rank,
      change: year2Entry.rank - year1Entry.rank,
    };
  }).sort((a, b) => Math.abs(b.change) - Math.abs(a.change)); // Sort by biggest changes

  return {
    year1,
    year2,
    year1Data,
    year2Data,
    commonCWEs,
    newInYear2,
    droppedFromYear2,
    rankChanges,
  };
}

/**
 * Get biggest movers (positive or negative) between most recent years
 */
export function getBiggestMovers(limit: number = 5): {
  improvers: YearComparison['rankChanges'];
  decliners: YearComparison['rankChanges'];
} {
  const years = AVAILABLE_YEARS.sort((a, b) => b - a);
  if (years.length < 2) {
    return { improvers: [], decliners: [] };
  }

  const comparison = compareYears(years[1], years[0]);
  if (!comparison) {
    return { improvers: [], decliners: [] };
  }

  // Improvers: negative change (moved up in rank, lower number = better)
  const improvers = comparison.rankChanges
    .filter((c) => c.change < 0)
    .slice(0, limit);

  // Decliners: positive change (moved down in rank, higher number = worse)
  const decliners = comparison.rankChanges
    .filter((c) => c.change > 0)
    .slice(0, limit);

  return { improvers, decliners };
}
